# Option 1: Ad Selection Panel

Adopt the new [Ad Selection Panel] by including its script tag on the webpage where the panel will be displayed:

```diff label="HTML"
<script
  type="text/javascript"
- src="https://seekcdn.com/hirer/indirect-posting/product-selection-panel/seek.js"
+ src="https://integration.seek.com/panels/SeekApi.js"
></script>
```

import {
  IconDelete,
  IconEdit,
  IconMinus,
  IconPromote
} from 'braid-design-system';

While its visual refresh will be immediately noticeable,
the new Ad Selection Panel also integrates with your software differently under the hood.

1. The panel is rendered via `SeekApi.render`:

   ```diff label="JavaScript"
   - SEEKAdPostingWidget.render(containerNode, options);
   + SeekApi.render(containerNode, 'adSelection', props);
   ```

   The render function must be called on page load and whenever the properties of the [`positionProfile`] input change.
   For example, if the hirer selects a new location, you must re-render the panel to reflect updated products and pricing.

2. The render function accepts a revised set of `props` which you must update in your software:

   | Legacy option                              | New prop                                                                                                                                                                                                                                                                                                         |
   | :----------------------------------------- | :--------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------- |
   |                                            | <p><IconPromote tone="promote" /> `locale`</p><p>Add this new option to specify the locale to display content in, e.g. `en-AU`. Set this prop to override the default localisation behaviour based on users browser preferences. Supported locales are outlined in the [content localisation] documentation.</p> |
   |                                            | <p><IconPromote tone="promote" /> `selectedAdvertisementProductId`</p><p>Add this new option to specify the product ID stored in a job ad draft</p>                                                                                                                                                              |
   |                                            | <p><IconPromote tone="promote" /> `sellingPointsDisplay`</p><p>New visual option defining how the panel should display selling points of each product</p>                                                                                                                                                        |
   | `draftAdvertisement`                       | <p><IconEdit tone="formAccent" /> `positionProfile`</p><p>Job ad fields have been revised, you must adjust the individual fields below.</p>                                                                                                                                                                      |
   | ~~`draftAdvertisement.typeCode`~~          | <p><IconDelete /></p><p>Remove, this prop is no longer supported</p>                                                                                                                                                                                                                                             |
   | ~~`draftAdvertisement.hirerJobReference`~~ | <p><IconDelete /></p><p>Remove, this prop is no longer supported</p>                                                                                                                                                                                                                                             |
   | `draftAdvertisement.positionTitle`         | <p><IconMinus tone="secondary" />`positionProfile.positionTitle`</p><p>Same behaviour as before</p>                                                                                                                                                                                                              |
   | `draftAdvertisement.jobCategoryId`         | <p><IconEdit tone="formAccent" />`positionProfile.jobCategories`</p><p>Pass in a single SEEK Category ID</p><p>e.g. `'seekAnzPublicTest:jobCategory:seek:2PiXemmou'`</p>                                                                                                                                         |
   | `draftAdvertisement.positionLocationId`    | <p><IconEdit tone="formAccent" />`positionProfile.positionLocation`</p><p>Pass in a single SEEK Location ID</p><p>e.g. `'seekAnzPublicTest:location:seek:W7NggeKH'`</p>                                                                                                                                          |
   |                                            | <p><IconPromote tone="promote" />`positionProfile.positionOrganizations`</p><p>Add this new prop containing a single SEEK Hirer ID that is posting or updating the job ad</p><p>e.g. `'seekAnzPublicTest:organization:seek:93WyyF1h'`</p>                                                                        |
   |                                            | <p><IconPromote tone="promote" />`positionProfile.offeredRemunerationPackage`</p><p>Add a new [`RemunerationPackageInput` object] containing remuneration offered for the position</p>                                                                                                                           |
   |                                            | <p><IconPromote tone="promote" />`positionProfile.profileId`</p><p>During updates, add this prop with the job ad ID to be updated (replaces functionality in previous `advertisement`)</p>                                                                                                                       |
   |                                            | <p><IconPromote tone="promote" />`positionProfile.seekAnzWorkTypeCode`</p><p>Add a new prop containing the [SEEK work type code]</p><p>e.g. `FullTime`</p>                                                                                                                                                       |
   | `onChange`                                 | <p><IconEdit tone="formAccent" /> `onChange`</p><p>Revise handling of the product fields in `event`, refer to step 4 below</p>                                                                                                                                                                                   |
   | `isValidationEnabled`                      | <p><IconMinus tone="secondary" /> `showFormValidationError`</p><p>Same behaviour as before</p>                                                                                                                                                                                                                   |
   | `getAuthToken`                             | <p><IconMinus tone="secondary" /> `getAuthToken`</p><p>Same behaviour as before</p>                                                                                                                                                                                                                              |
   | ~~`advertisement`~~                        | <p><IconDelete /></p><p>Remove, information for existing job ads being updated is now inferred from `positionProfile.profileId`</p>                                                                                                                                                                              |
   | ~~`disableCSSOMInjection`~~                | <p><IconDelete /></p><p>Remove, this prop is no longer supported</p>                                                                                                                                                                                                                                             |
   | ~~`instanceId`~~                           | <p><IconDelete /></p><p>Remove, this prop is no longer supported</p>                                                                                                                                                                                                                                             |
   | ~~`mode`~~                                 | <p><IconDelete /></p><p>Remove, an update is now inferred by the presence of a `positionProfile.profileId`</p>                                                                                                                                                                                                   |
   | ~~`onInitialLoad`~~                        | <p><IconDelete /></p><p>Remove, fallback is no longer supported</p>                                                                                                                                                                                                                                              |
   | ~~`theme`~~                                | <p><IconDelete /></p><p>Remove, default theme is brand neutral</p>                                                                                                                                                                                                                                               |

[content localisation]: /graphql/in-practice#content-localisation
[SEEK work type code]: /schema/named-type/AdvertisementProducts_PositionProfileInput#/field/seekAnzWorkTypeCode
[`RemunerationPackageInput` object]: /schema/named-type/AdvertisementProducts_PositionProfileInput#/field/offeredRemunerationPackage

**Example**

```javascript label="New Ad Selection Panel"
SeekApi.render(document.getElementById('seekPanelContainer'), 'adSelection', {
  getAuthToken: async () => {
    // Do not implement caching in your `getAuthToken` implementation.
    // The panel will internally memoise the response.
    const token = await fetchAuthToken();

    return token;
  },

  onChange: (event) => {
    const { selectedProduct } = event;

    // Persist `selectedProduct.id` in memory for use in job posting mutations.
    //

    // Display form inputs for features supported by the selected product.

    if (selectedProduct?.features.branding) {
      // Show brand selection.
    } else {
      // Hide brand selection.
    }

    if (selectedProduct?.features.searchBulletPoints) {
      // Show the number of bullet point input fields indicated by `limit`.
    } else {
      // Hide all bullet point input fields.
    }
  },

  sellingPointsDisplay: 'bullet',

  positionProfile: {
    // Ad products and their pricing vary based on these classifiers.
    jobCategories: 'seekAnzPublicTest:jobCategory:seek:27HXTkNXh',
    positionLocation: 'seekAnzPublicTest:location:seek:W7NggeKH',
    positionOrganizations: 'seekAnzPublicTest:organization:seek:93WyyF1h',
    positionTitle: 'Software Engineer',

    // Salary details let us indicate how each ad product is likely to perform.
    offeredRemunerationPackage: {
      basisCode: 'Salaried',
      descriptions: ['Up to $70,000 per year + bonus'],
      ranges: [
        {
          intervalCode: 'Year',
          minimumAmount: { currency: 'AUD', value: 40000 },
          maximumAmount: { currency: 'AUD', value: 70000 }
        }
      ]
    },
    seekAnzWorkTypeCode: 'FullTime',

    // The position profile ID should be provided when editing a job ad.
    profileId: undefined
  },

  // The product ID stored in a job ad draft.
  selectedAdvertisementProductId: undefined,

  // Set this to true if the hirer tries to post the job ad before they have
  // selected an ad product (i.e. no `selectedProduct.id` persisted in memory).
  showFormValidationError: false
});
```

```javascript label="Previous Implementation"
SEEKAdPostingWidget.render(document.getElementById('seekWidget1ContainerDiv'), {
  getAuthToken: function () {
    return Promise.resolve('browser token');
  },
  //
  //
  //
  //
  //
  onChange: function (event) {
    const { advertisement } = event;

    // Persist `typeCode` in memory for job posting mutations.
    const { typeCode, features } = advertisement;

    const { brandingIndicator, searchBulletPointsIndicator } = features;

    if (brandingIndicator) {
      // Show brand selector
    } else {
      // Hide brand selector
    }

    if (searchBulletPointsIndicator) {
      // Show fields to enter bullet points
    } else {
      // Hide bullet point fields
    }
  },

  // `draftAdvertisement` should contain all of the hirer’s
  // current inputs if they are posting or updating.
  draftAdvertisement: {
    jobCategoryId: 'seekAnzPublicTest:jobCategory:seek:2EFstqFvP',
    positionLocationId: 'seekAnzPublicTest:location:seek:2TCFhBtw9',
    hirerJobReference: 'Premium521',
    positionTitle: 'Developer'
  },

  advertisement: {
    // Position profile ID
    id: 'seekAnzPublicTest:positionProfile:jobAd:27cuZeA47'
  },

  theme: {
    selectionColor: '#e60278',
    linkColor: '#e60278',
    cautionColor: '#b38800',
    errorColor: '#ff0000'
  },

  mode: 'Create',

  onInitialLoad: function (event) {
    const { instanceId, isVisible } = event;
    if (isVisible) {
      console.log(`Widget ${instanceId} loaded`);
    }
  }
});
```

The new [`positionProfile`] prop has been extended to accept salary details.
We recommend rendering ad selection after you capture [salary information] in your posting workflow.
This will enable your software to supply the optional `offeredRemunerationPackage` and `seekAnzWorkTypeCode` inputs,
which SEEK can use to better predict—and inform the user of—how each ad product is likely to perform.

3. The `onChange` callback returns an [object identifier] for the selected product.

   A human-readable `advertisement.typeCode` is no longer provided.
   The object identifier should be propagated to the `seekAdvertisementProductId` field when previewing, posting or updating the job ad.

   ```diff label="JavaScript"
   const event = {
   - advertisement: {
   + selectedProduct: {
   -   typeCode: 'StandOut',
   +   id: 'globalPublicTest:adProduct:seekApi:11111111111111111111111111111111',
       // ...
     },
   };
   ```

4. The `onChange` callback returns the features supported by the selected product.

   Your software must conditionally display brand selection and search bullet point inputs based on these dynamic feature indicators.
   You may have a similar capability if you integrated with the feature indicators of the previous Ad Selection Panel,
   but we now provide parameters that detail which brand components and how many bullet points are supported:

   ```diff label="JavaScript"
   const event = {
   - advertisement: {
   + selectedProduct: {
       features: {
   -     brandingIndicator: true,
   +     branding: {
   +       coverImageIndicator: false,
   +       logoIndicator: true,
   +     },
   -     searchBulletPointsIndicator: true,
   +     searchBulletPoints: {
   +       limit: 3,
   +     },
       },
       // ...
     },
   };
   ```

5. A hardcoded fallback field is no longer correct.

   Error handling is now largely managed within the panel itself.
   If you implemented a fallback for the previous panel you can now remove it.

6. Internet Explorer 11 is no longer supported.

   We have introduced a new [browser support policy].
   We are not able to provide ongoing support for Internet Explorer as it is retired and out of support.

You can preview the new Ad Selection Panel below,
and see our [panel implementation guide][ad selection panel] for more information.

import { AdSelectionPanel } from '../../../../use-cases/job-posting/ad-selection/v2/AdSelectionPanel';

<AdSelectionPanel mode="panelDev" />

[`positionprofile`]: /schema/query/advertisementProducts#/argument/positionProfile
[salary information]: /use-cases/job-posting/salary
[ad selection panel]: /use-cases/job-posting/ad-selection/v2/panel
[object identifier]: /graphql/seek-api-conventions#object-identifiers
[browser support policy]: /introduction/browser-support

## Managing variable job ads

Due to the variable nature of SEEK’s product ladder,
the new panel and GraphQL query return ephemeral product IDs that reference the set of products quoted at that point in time.
When a hirer selects a product and your software propagates its identifier to post or update a job ad,
SEEK will scan recent ad selection contexts for a match and attempt to lock in the exact quoted product features and pricing.

Ad products presented to a hirer are only valid within the context of the workflow that they were executing at the time.
Product IDs cannot be persisted long term;
they should not be stored alongside a posted job ad for [subsequent update workflows] or to otherwise build a hardcoded mapping.

The selected product ID may be temporarily stored in a job ad draft.
When the hirer resumes their posting workflow from the draft at a later date,
this previous product ID should not be directly used to post the job ad.
Instead, pass the previous product ID to ad selection as the `selectedAdvertisementProductId`.
Ad selection will then pre-select an equivalent ad product and return new product IDs that can be used to post the job ad.

[subsequent update workflows]: /use-cases/job-posting/managing-job-ads/updating-a-job-ad

### Posting a job ad

Your software must omit the deprecated `seekAnzAdvertisementType` field in favour of a [`seekAdvertisementProductId`][create-product-id] sourced from our new panel or GraphQL query.
Your software should always [omit unused arguments] as a best practice to reduce the surface area of your integration.
Setting `seekAnzAdvertisementType` will result in failed Job Posting create and update calls following the December 2024 retirement date.

```json label="Before"
{
  "postingInstructions": {
    "seekAnzAdvertisementType": "Classic"
  }
}
```

```jsonc label="After (correct)"
{
  "postingInstructions": {
    // Correct: omit unused argument

    "seekAdvertisementProductId": "Source from our new panel or GraphQL query"
  }
}
```

```jsonc label="After (incorrect)"
{
  "postingInstructions": {
    // Incorrect: set unused argument to null
    "seekAnzAdvertisementType": null,

    "seekAdvertisementProductId": "Source from our new panel or GraphQL query"
  }
}
```

See the [relevant use case documentation](/use-cases/job-posting/managing-job-ads/posting-a-job-ad) for more information.

[create-product-id]: /schema/named-type/CreatePostingInstructionInput#/field/seekAdvertisementProductId
[omit unused arguments]: /graphql/in-practice#optional-arguments

### Querying a job ad

If your software retrieves the current state of a job ad from the SEEK API,
it must omit the deprecated `seekAnzAdvertisementType` field in favour of the new [`seekAdvertisementProduct`] field.
Your software should [only select the fields it uses] as a best practice to reduce the surface area of your integration.
Selecting `seekAnzAdvertisementType` will result in failed Job Posting queries following the December 2024 retirement date.

```graphql label="Before" lovato-ignore-no-inline-graphql-arguments lovato-ignore-valid-graphql-operation
# Inline arguments are only used to simplify these examples.
# Parameterise your production queries with variables.
# https://developer.seek.com/graphql/in-practice#variables

query {
  positionProfile(id: "seekAnzPublicTest:positionProfile:jobAd:2782PZfXV") {
    postingInstructions {
      seekAnzAdvertisementType
    }
  }
}
```

```graphql label="After (correct)" lovato-ignore-no-inline-graphql-arguments
# Inline arguments are only used to simplify these examples.
# Parameterise your production queries with variables.
# https://developer.seek.com/graphql/in-practice#variables

query {
  positionProfile(id: "seekAnzPublicTest:positionProfile:jobAd:2782PZfXV") {
    postingInstructions {
      # Correct: omit unused field

      seekAdvertisementProduct {
        label
      }
    }
  }
}
```

```graphql label="After (incorrect)" lovato-ignore-no-inline-graphql-arguments lovato-ignore-valid-graphql-operation
# Inline arguments are only used to simplify these examples.
# Parameterise your production queries with variables.
# https://developer.seek.com/graphql/in-practice#variables

query {
  positionProfile(id: "seekAnzPublicTest:positionProfile:jobAd:2782PZfXV") {
    postingInstructions {
      # Incorrect: select unused field
      seekAnzAdvertisementType

      seekAdvertisementProduct {
        label
      }
    }
  }
}
```

[`seekAdvertisementProduct`]: /schema/named-type/PostingInstruction#/field/seekAdvertisementProduct
[only select the fields it uses]: /graphql/in-practice#selecting-fields

### Updating a job ad

In a typical update workflow,
you should present an ad selection interface to the hirer so they can choose to upgrade their ad product.

Your software must omit the deprecated `seekAnzAdvertisementType` field in favour of a [`seekAdvertisementProductId`][update-oid] sourced from our new panel or GraphQL query.
Using `seekAnzAdvertisementType` will result in failed Job Posting create and update calls following the December 2024 retirement date.

Under certain circumstances,
it may be infeasible to present an ad selection interface to the hirer when an update is made.
For example, the update may be driven by a back-end process in your software rather than an individual user interacting with an update workflow.
In such scenarios, you should omit both [`seekAdvertisementProductId`][update-oid] and `seekAnzAdvertisementType` to retain the current ad product.

See the [relevant use case documentation](/use-cases/job-posting/managing-job-ads/updating-a-job-ad) for more information.

[update-oid]: /schema/named-type/UpdatePostingInstructionInput#/field/seekAdvertisementProductId

## Exit criteria

The following criteria need to be met before progressing to the next feature:

| Criteria                           | Description                                                                                                          |
| ---------------------------------- | -------------------------------------------------------------------------------------------------------------------- |
| Ad selection built                 | SEEK hirers can select available SEEK ad products and see pricing when creating or editing a job ad in your software |
| Ad selection re-render implemented | SEEK hirers can see new products when the position location, job category or title has changed                       |
| Ad selection fallback removed      | SEEK hirers must not see a dropdown list of hardcoded ad types when the Ad Selection Panel fails to load             |
| User experience signed off         | SEEK has signed off on your ad selection user experience                                                             |
| Job ad posted successfully         | Hirer can successfully post a job ad to SEEK with their selected ad product                                          |
| Job ad updated successfully        | Hirer can successfully update a job ad to SEEK with their selected ad product                                        |

## Troubleshooting

See our general [panel troubleshooting] documentation for more information.

[panel troubleshooting]: /introduction/panels#troubleshooting
